<script>
  import { _ } from 'svelte-i18n'
  import { tick } from 'svelte'
  import { modals, previews_font_size, previews_are_zoomed } from '../../stores'
  import { cloneForPrintPreview } from '../../modules/exportPreviews'
  import HorizontalOptionsList from '../../elements/HorizontalOptionsList.svelte'
  import Fieldset from '../../elements/Fieldset.svelte'
  import Button from '../../elements/Button.svelte'
  import Modal from '../../elements/Modal.svelte'
  import Label from '../../elements/Label.svelte'
  import PreviewsSizeStepper from '../PreviewsSizeStepper.svelte'
  
  const modal_id = 'print'
  
  let modal_is_visible
  let preview_current_page = 0

  let print_preview_el
  let preview_wrapper_el
  
  let paper_sizes = [{name:'letter', long:'279', short:'216'}, {name:'a4', long:'297', short:'210'}]
  let paper_size_options = [{value: 0, display:$_('modals.print.letter')}, {value: 1, display:$_('modals.print.a4')}]
  let orientation_options = [{value: 'landscape', display:$_('modals.print.landscape')}, {value: 'portrait', display:$_('modals.print.portrait')}]
  
  let prefered_orientation = 'landscape'
  let prefered_paper_size_index = 0
  let paper_width
  let paper_height
  let print_margin_mm = 10
  let preview_total_pages

  $: {
    if (modal_is_visible) {
      prefered_paper_size_index, 
      prefered_orientation, 
      $previews_font_size,
      prepareForPrint()
    }

    if (prefered_orientation === 'landscape') {
      paper_width = paper_sizes[prefered_paper_size_index].long
      paper_height = paper_sizes[prefered_paper_size_index].short
    } 
    else {
      paper_width = paper_sizes[prefered_paper_size_index].short
      paper_height = paper_sizes[prefered_paper_size_index].long
    }
  }

  $: prefered_orientation, prefered_paper_size_index, writePageRules()

  async function prepareForPrint() {
    await(tick)
    window.document.body.classList.add('printable')
    // Clear existing content first
    if (preview_wrapper_el) {
      preview_wrapper_el.innerHTML = '';
    }
    cloneForPrintPreview(paper_width, paper_height, print_margin_mm, preview_wrapper_el, scalePreview)
  }

  function donePrinting() {
    preview_current_page = 0
    preview_total_pages = undefined
    modals.close()
  }

  function scalePreview() {
    let scale = 1
    let increment = 0.001
    let parent_width = preview_wrapper_el.parentElement.getBoundingClientRect().width
    while(preview_wrapper_el.getBoundingClientRect().width % parent_width > 3) {
      if(preview_wrapper_el.getBoundingClientRect().width > parent_width) {
        scale -= increment
      } else {
        scale += increment
      }
      preview_wrapper_el.style.transform = 'scale('+scale+')'
    }
    if (!$previews_are_zoomed) paginatePreview()
  }

  function paginatePreview() {
    const preview_height = preview_wrapper_el.firstChild.clientHeight
    const preview_margin_px = parseInt(window.getComputedStyle(preview_wrapper_el.firstChild, null).getPropertyValue('padding'))
    const elements = preview_wrapper_el.querySelectorAll('.preview')
    // remove elements that are outside the preview area
    for (let i = 0; i < elements.length; i++) {
      const element = elements[i]
      if(element.offsetTop + element.clientHeight > preview_height - preview_margin_px) {
        element.remove()
      }
    }
  }

  function writePageRules() {
    if(document.getElementById('page_rules')) document.getElementById('page_rules').remove()
    
    let style = document.createElement('style')
    style.type = 'text/css'
    style.id = 'page_rules'
    document.head.append(style)
    
    let rules = '@page { size: '
    rules += paper_sizes[prefered_paper_size_index].name + ' '
    rules += prefered_orientation + '; '
    rules += 'margin: '
    rules += print_margin_mm + 'mm; '
    rules += '}'

    style.appendChild(document.createTextNode(rules))
  }

  function updateCurrentPageDisplay() {
    const ph = preview_wrapper_el.firstChild.getBoundingClientRect().height
    preview_current_page = Math.round(print_preview_el.scrollTop / ph)
  }

</script>

<Modal id={modal_id} bind:is_visible={modal_is_visible} on:modalClosed={donePrinting}>
  
  <span slot="header">
    {$previews_are_zoomed ? $_('modals.print.titleSingle') : $_('modals.print.title')}
  </span>
  
  <span slot="content">
    <p>{$_('modals.print.intro')}</p>
    <p class="faded">{$_('modals.print.reminder')}</p>
    <Fieldset>
      <Label>{$_('modals.print.paperSize')}</Label>
      <HorizontalOptionsList list_options={paper_size_options} bind:selected_option={prefered_paper_size_index}/>  
    </Fieldset>
    <Fieldset>
      <Label>{$_('modals.print.orientation')}</Label>
      <HorizontalOptionsList list_options={orientation_options} bind:selected_option={prefered_orientation}/>  
    </Fieldset>
    {#if !$previews_are_zoomed}
      <Fieldset>
        <Label>{$_('modals.print.itemSize')}</Label>
        <PreviewsSizeStepper/>
      </Fieldset>
    {/if}
  </span>

  <span slot="footer">
    <Button kind="accent" on:click={() => window.print()}>{$_('buttons.print')}</Button>
    &nbsp;
    <Button kind="outline" on:click={donePrinting}>{$_('buttons.close')}</Button>
  </span>

  <span slot="preview">
    <div 
      bind:this={print_preview_el} 
      on:scroll={updateCurrentPageDisplay} 
      class="print_preview {prefered_orientation} {paper_size_options[prefered_paper_size_index].display.toLowerCase()}">
      <div class="preview_wrapper_el" bind:this={preview_wrapper_el}></div>
    </div>
  </span>
</Modal>

<style>
  .preview_page_number {
    position: absolute;
    bottom: 1rem;
    left: 50%;
    transform: translate(-50%, 0);
    padding: 0.5rem 1rem;
    font-size: 1.5rem;
    font-variant-numeric: lining-nums;
    line-height: 2rem;
    color: var(--c-text-on-fill);
    background-color: var(--c-flash-bg);
    border-radius: 1.5rem;
  }
  p {
    margin-bottom: 2rem;
  }
  p.faded {
    color: var(--c-text-faded);
  }
  .print_preview {
    position: relative;
    height: 0;
    overflow-x: hidden;
    overflow-y: scroll;
  }
  .print_preview.landscape.a4 {
    padding-top: 70.71%;
  }
  .print_preview.portrait.a4 {
    height: 580px;
    width: 410px;
    margin: auto;
  }
  .print_preview.landscape.letter {
    padding-top: 77.41%;
  }
  .print_preview.portrait.letter {
    height: 580px;
    width: 449px;
    margin: auto;
  }

  .preview_wrapper_el {
    position: absolute;
    top: 0;
    transform-origin: top left;
  }
  .preview_wrapper_el :global(img) {
    display: 'block';
    width: 100%;
  }

  .preview_wrapper_el :global(.scrollToTopButton),
  .preview_wrapper_el :global(.floatingCloseButton),
  .preview_wrapper_el :global(.stickyWrapper) {
    display: none;
  }

  .preview_wrapper_el :global(.previews) {
    display: block !important;
    background-color: white !important;
    pointer-events: none;
  }
  .preview_wrapper_el :global(.preview) {
    color: black !important;
    background-color: white !important;
  }
  .preview_wrapper_el :global(.preview *) {
    color: black !important;
  }
  .preview_wrapper_el :global(.preview:not(.zoomed)) {
    display: inline-block !important;
    margin: 0.6rem !important;
    border: 2px solid var(--c-bg);
  }
  .preview_wrapper_el :global(.preview.zoomed) {
    position: unset;
    border-width: 0 !important;
  }
  .preview_wrapper_el :global(.fontname) {
    opacity: 0.5 !important;
    left: 0;
    width: 100%;
    max-width: none;
    padding: 0 0.5rem;
  }
</style>